import { BRAND } from "./site";

const CACHE_KEY = "sigspoon_carousel_items";
const CACHE_TIME_KEY = "sigspoon_carousel_timestamp";

const TESTIMONIALS_CACHE_KEY = "sigspoon_testimonials_items";
const TESTIMONIALS_CACHE_TIME_KEY = "sigspoon_testimonials_timestamp";

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache

function optimizeImageUrl(url) {
  if (!url) return "";
  let cleanUrl = String(url).trim();

  // Automatically optimize Cloudinary URLs for ultra-fast loading:
  // f_auto = automatic WebP/AVIF format based on browser
  // q_auto = smart compression reducing 80%+ file size
  // w_800 = resize 4K/heavy images to fit 800px web card width
  if (cleanUrl.includes("res.cloudinary.com") && cleanUrl.includes("/upload/")) {
    if (!cleanUrl.includes("f_auto") && !cleanUrl.includes("q_auto")) {
      cleanUrl = cleanUrl.replace("/upload/", "/upload/f_auto,q_auto,w_800,c_limit/");
    }
  }
  return cleanUrl;
}

export async function fetchGoogleSheetCarousel(defaultCats = []) {
  const sheetId = process.env.REACT_APP_GOOGLE_SHEET_ID || BRAND.googleSheetId;

  if (!sheetId) {
    return defaultCats;
  }

  // Check session cache first for fast page reloads
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    const timestamp = sessionStorage.getItem(CACHE_TIME_KEY);
    if (cached && timestamp && Date.now() - parseInt(timestamp, 10) < CACHE_TTL_MS) {
      return JSON.parse(cached);
    }
  } catch (e) {
    // Ignore cache errors
  }

  // Try fetching sheet tab named 'Carousel', fallback to 'Sheet1'
  const sheetNames = ["Carousel", "Sheet1"];
  let rawRows = null;

  for (const sheetName of sheetNames) {
    try {
      const res = await fetch(`https://opensheet.elk.sh/${sheetId}/${sheetName}`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          rawRows = data;
          break;
        }
      }
    } catch (err) {
      console.warn(`Failed to fetch sheet tab '${sheetName}':`, err);
    }
  }

  if (!rawRows || rawRows.length === 0) {
    return defaultCats;
  }

  // Map Google Sheet rows to carousel item schema
  const sheetItems = rawRows
    .filter((row) => row.title && (row.img || row.image))
    .map((row, idx) => {
      const tagStr = row.tag ? String(row.tag).trim() : `0${idx + 1}`;
      const badgeStr = row.badge ? String(row.badge).trim() : "Latest Item";
      const itemsArr = row.items
        ? String(row.items)
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
        : ["Fresh Bake"];

      return {
        tag: tagStr,
        badge: badgeStr,
        title: String(row.title).trim(),
        desc: row.desc ? String(row.desc).trim() : "",
        items: itemsArr,
        microTag: row.microTag ? String(row.microTag).trim() : "100% Pure Vegetarian • Fresh Daily",
        img: optimizeImageUrl(row.img || row.image),
        msg: row.msg
          ? String(row.msg).trim()
          : `Hi! I'd like to inquire/order ${String(row.title).trim()}.`,
        isDynamic: true,
      };
    });

  // Prepend dynamic Google Sheet items ahead of default categories
  const combined = [...sheetItems, ...defaultCats];

  // Save to session cache
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(combined));
    sessionStorage.setItem(CACHE_TIME_KEY, String(Date.now()));
  } catch (e) {
    // Ignore storage quota errors
  }

  return combined;
}

export async function fetchGoogleSheetTestimonials(defaultReviews = []) {
  const sheetId = process.env.REACT_APP_GOOGLE_SHEET_ID || BRAND.googleSheetId;

  if (!sheetId) {
    return defaultReviews;
  }

  // Check session cache first
  try {
    const cached = sessionStorage.getItem(TESTIMONIALS_CACHE_KEY);
    const timestamp = sessionStorage.getItem(TESTIMONIALS_CACHE_TIME_KEY);
    if (cached && timestamp && Date.now() - parseInt(timestamp, 10) < CACHE_TTL_MS) {
      return JSON.parse(cached);
    }
  } catch (e) {
    // Ignore cache errors
  }

  // Try fetching sheet tab named 'Testimonials', fallback to 'Reviews' or 'Sheet2'
  const sheetNames = ["Testimonials", "Reviews", "Sheet2"];
  let rawRows = null;

  for (const sheetName of sheetNames) {
    try {
      const res = await fetch(`https://opensheet.elk.sh/${sheetId}/${sheetName}`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          rawRows = data;
          break;
        }
      }
    } catch (err) {
      // Try next sheet name
    }
  }

  if (!rawRows || rawRows.length === 0) {
    return defaultReviews;
  }

  // Map Google Sheet rows to review schema
  const sheetReviews = rawRows
    .filter((row) => (row.name || row.Name) && (row.quote || row.Quote || row.review || row.Review))
    .map((row) => {
      const quoteStr = String(row.quote || row.Quote || row.review || row.Review).trim();
      const nameStr = String(row.name || row.Name).trim();
      const whereStr = row.where || row.Where || row.location || row.Location ? String(row.where || row.Where || row.location || row.Location).trim() : "";
      const ratingNum = parseInt(row.rating || row.Rating, 10) || 5;

      return {
        quote: quoteStr,
        name: nameStr,
        where: whereStr,
        rating: ratingNum,
        isDynamic: true,
      };
    });

  // Prepend new dynamic Google Sheet reviews ahead of default reviews
  const combined = [...sheetReviews, ...defaultReviews];

  // Save to session cache
  try {
    sessionStorage.setItem(TESTIMONIALS_CACHE_KEY, JSON.stringify(combined));
    sessionStorage.setItem(TESTIMONIALS_CACHE_TIME_KEY, String(Date.now()));
  } catch (e) {
    // Ignore storage quota errors
  }

  return combined;
}
